import { supabase } from "@/lib/supabase";
import { SupabaseTableEnum } from "@/lib/supabase/supabaseTypes";
import { create } from "zustand";
import { useCentralStore } from "../Central";
import { useCatalogsStore } from "../Catalogs";
import { Logger } from "@/lib/logger/Logger";
import { Tables } from "@/lib/supabase/supabaseTypesImport";
import { showNotification } from "../Central/selectors";
import { produce } from "immer";
import { useServiceCatalogStore } from "../ServiceCatalog";

/**
 * Templates are a collection of tariffs and articles that can be added to a job document at once via the template code or by selecting the template.
 * This store manages templates and their items of the current organization.
 *
 * How they work:
 * - One "template" has many "template_items"
 * - "template_items" reference tariffs/articles via foreign keys (tariffs_default_id, tariffs_custom_id, article_id)
 * - If a user wants to customize more about a template (e.g. the item should have a specific description, tax, etc.) they need to create different
 *   tariff/article variants (for tariffs by using a different code_e) and use those in the template. This is important to keep the simplicity of what
 *   templates are: a collection of tariff/article references with their quantity.
 */

export type Template = Omit<Tables<"templates">, "organization_id" | "blo_id">;
export type TemplateItem = Omit<
	Tables<"template_items">,
	"template_id" | "modified_at" | "created_at" | "bpo_id" | "row_number"
>;

export type TemplateItemType = "tariff_default" | "tariff_custom" | "article";

export interface TemplateItemInput {
	quantity: number;
	code_e?: string;
	tariffs_default_id?: string | null;
	tariffs_custom_id?: string | null;
	article_id?: number | null;
}

export interface TemplateWithItems extends Template {
	template_items: TemplateItem[];
}

const COLUMNS_FOR_TEMPLATE = "id, code, name, created_at, modified_at";
const COLUMNS_FOR_TEMPLATE_ITEM =
	"id, code_e, quantity, tariffs_default_id, tariffs_custom_id, article_id";
const COLUMNS_FOR_TEMPLATE_WITH_ITEMS = `${COLUMNS_FOR_TEMPLATE}, template_items(${COLUMNS_FOR_TEMPLATE_ITEM})`;

const findExistingItem = (
	items: TemplateItem[],
	input: TemplateItemInput,
	excludeIds?: Set<number>
): TemplateItem | undefined => {
	const match = (existing: TemplateItem) =>
		!excludeIds || !excludeIds.has(existing.id);
	if (input.tariffs_default_id)
		return items.find(
			(e) => e.tariffs_default_id === input.tariffs_default_id && match(e)
		);
	if (input.tariffs_custom_id)
		return items.find(
			(e) => e.tariffs_custom_id === input.tariffs_custom_id && match(e)
		);
	if (input.article_id)
		return items.find((e) => e.article_id === input.article_id && match(e));
	return undefined;
};

interface TemplatesState {
	templatesLookup: Record<number, TemplateWithItems>;
	fetchTemplates: () => Promise<void>;
	createTemplate: (
		code: string,
		name: string,
		items: TemplateItemInput[]
	) => Promise<TemplateWithItems | undefined>;
	deleteTemplate: (id: number) => Promise<void>;
	updateTemplateName: (id: number, name: string) => Promise<void>;
	updateTemplateCode: (id: number, code: string) => Promise<void>;
	updateTemplateItemQuantity: (
		templateId: number,
		itemId: number,
		quantity: number
	) => Promise<void>;
	addTemplateItem: (
		templateId: number,
		item: TemplateItemInput
	) => Promise<void>;
	deleteTemplateItem: (templateId: number, itemId: number) => Promise<void>;
	pasteTemplateItems: (
		items: TemplateItemInput[],
		templateId: number,
		forceDuplicate?: boolean
	) => Promise<void>;
	checkTemplateUsage: (filter: {
		tariffs_default_id?: string;
		tariffs_custom_id?: string;
		article_id?: number;
	}) => Promise<number[]>;
	reassignTemplateItems: (
		from: { tariffs_default_id?: string; tariffs_custom_id?: string },
		to: {
			tariffs_default_id?: string | null;
			tariffs_custom_id?: string | null;
		}
	) => Promise<number>;
	removeItemFromTemplates: (filter: {
		tariffs_custom_id?: string;
		article_id?: number;
	}) => Promise<number>;
}

export const useTemplatesStore = create<TemplatesState>()((set) => ({
	templatesLookup: {},
	fetchTemplates: async () => {
		const organizationId = useCentralStore.getState().organization?.id;
		if (!organizationId) {
			Logger.error("No organization id found", {});
			return;
		}
		const { data, error } = await supabase
			.from("templates")
			.select(COLUMNS_FOR_TEMPLATE_WITH_ITEMS)
			.eq("organization_id", organizationId);

		if (error || !data) {
			Logger.error(error, {}, "Error fetching templates");
			return;
		}
		set({
			templatesLookup: data.reduce(
				(
					acc: Record<number, TemplateWithItems>,
					template: TemplateWithItems
				) => {
					acc[template.id] = {
						...template,
						template_items: Array.isArray(template.template_items)
							? template.template_items
							: [],
					};
					return acc;
				},
				{} as Record<number, TemplateWithItems>
			),
		});
	},
	createTemplate: async (
		code: string,
		name: string,
		items: TemplateItemInput[]
	) => {
		const { data: newTemplate, error } = await supabase
			.from(SupabaseTableEnum.TEMPLATES)
			.insert({
				code,
				name,
			})
			.select(COLUMNS_FOR_TEMPLATE)
			.single();

		if (error) {
			Logger.error(error, {}, "Error creating template");
			showNotification({
				message: "Fehler beim Erstellen der Vorlage",
				type: "error",
			});
			return;
		}

		const tariffsLookup = useCatalogsStore.getState().tariffsLookup;
		const articles = useCentralStore.getState().articles;

		const resolvedItems = items.map((item) => {
			let tariffs_default_id = item.tariffs_default_id || null;
			let tariffs_custom_id = item.tariffs_custom_id || null;
			let article_id = item.article_id || null;

			if (
				item.code_e &&
				!tariffs_default_id &&
				!tariffs_custom_id &&
				!article_id
			) {
				const tariffsForCode = tariffsLookup[item.code_e];
				if (tariffsForCode && Object.keys(tariffsForCode).length > 0) {
					const tariff = Object.values(tariffsForCode)[0];
					if (tariff.is_custom) {
						tariffs_custom_id = tariff.id;
					} else {
						tariffs_default_id = tariff.id;
					}
				} else {
					const articlesForCode = articles[item.code_e];
					if (
						articlesForCode &&
						Object.keys(articlesForCode).length > 0
					) {
						const article = Object.values(articlesForCode)[0];
						article_id = article.id;
					}
				}
			}

			return {
				template_id: newTemplate.id,
				code_e: item.code_e || "",
				quantity: item.quantity,
				tariffs_default_id,
				tariffs_custom_id,
				article_id,
			};
		});

		const { data: templateItems, error: templateItemsError } =
			await supabase
				.from(SupabaseTableEnum.TEMPLATE_ITEMS)
				.insert(resolvedItems)
				.select(COLUMNS_FOR_TEMPLATE_ITEM);
		if (templateItemsError) {
			Logger.error(
				templateItemsError,
				{},
				"Error creating template items"
			);
			showNotification({
				message: "Fehler beim Erstellen der Vorlage",
				type: "error",
			});
			return;
		}

		const newTemplateWithItems: TemplateWithItems = {
			...newTemplate,
			template_items: templateItems,
		};
		set(
			produce((state) => {
				state.templatesLookup[newTemplateWithItems.id] =
					newTemplateWithItems;
			})
		);

		return newTemplateWithItems;
	},
	deleteTemplate: async (id: number) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.TEMPLATES)
			.delete()
			.eq("id", id);

		if (error) {
			Logger.error(error, {}, "Error deleting template");
			showNotification({
				message: "Fehler beim Löschen der Vorlage",
				type: "error",
			});
			return;
		}
		set(
			produce((state) => {
				delete state.templatesLookup[id];
			})
		);

		const catalogStore = useServiceCatalogStore.getState();
		const usage = catalogStore.checkItemUsage({ template_id: id });
		if (usage.length > 0) {
			catalogStore.removeItemGlobally({ template_id: id });
			await catalogStore.saveCatalog();
		}

		showNotification({
			message: "Vorlage erfolgreich gelöscht",
			type: "success",
		});
	},
	updateTemplateName: async (id: number, name: string) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.TEMPLATES)
			.update({ name })
			.eq("id", id);

		if (error) {
			Logger.error(error, {}, "Error updating template name");
			showNotification({
				message: "Fehler beim Aktualisieren der Vorlage",
				type: "error",
			});
			useTemplatesStore.getState().fetchTemplates();
			return;
		}

		set(
			produce((state) => {
				state.templatesLookup[id].name = name;
			})
		);
		showNotification({
			message: "Vorlage erfolgreich aktualisiert",
			type: "success",
		});
	},
	updateTemplateCode: async (id: number, code: string) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.TEMPLATES)
			.update({ code })
			.eq("id", id);

		if (error) {
			Logger.error(error, {}, "Error updating template code");
			showNotification({
				message: "Fehler beim Aktualisieren der Vorlage",
				type: "error",
			});
			useTemplatesStore.getState().fetchTemplates();
			return;
		}

		set(
			produce((state) => {
				state.templatesLookup[id].code = code;
			})
		);
		showNotification({
			message: "Vorlage erfolgreich aktualisiert",
			type: "success",
		});
	},
	updateTemplateItemQuantity: async (
		templateId: number,
		itemId: number,
		quantity: number
	) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.update({ quantity })
			.eq("id", itemId);

		if (error) {
			Logger.error(error, {}, "Error updating template item");
			showNotification({
				message: "Fehler beim Aktualisieren der Vorlage",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.templatesLookup[templateId].template_items.find(
					(item: any) => item.id === itemId
				).quantity = quantity;
			})
		);
		showNotification({
			message: "Vorlage erfolgreich aktualisiert",
			type: "success",
		});
	},
	deleteTemplateItem: async (templateId: number, itemId: number) => {
		const { error } = await supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.delete()
			.eq("id", itemId);

		if (error) {
			Logger.error(error, {}, "Error deleting template item");
			showNotification({
				message: "Fehler beim Löschen des Vorlagenelements",
				type: "error",
			});
			return;
		}

		set(
			produce((state) => {
				state.templatesLookup[templateId].template_items =
					state.templatesLookup[templateId].template_items.filter(
						(item: any) => item.id !== itemId
					);
			})
		);
		showNotification({
			message: "Vorlagenelement erfolgreich gelöscht",
			type: "success",
		});
	},
	addTemplateItem: async (templateId: number, item: TemplateItemInput) => {
		const templateItems =
			useTemplatesStore.getState().templatesLookup[templateId]
				?.template_items || [];
		const existingItem = findExistingItem(templateItems, item);

		if (existingItem) {
			set(
				produce((state) => {
					const itemToUpdate = state.templatesLookup[
						templateId
					].template_items.find(
						(i: TemplateItem) => i.id === existingItem.id
					);
					if (itemToUpdate) {
						itemToUpdate.quantity += item.quantity;
					}
				})
			);

			const { error } = await supabase
				.from(SupabaseTableEnum.TEMPLATE_ITEMS)
				.update({ quantity: existingItem.quantity + item.quantity })
				.eq("id", existingItem.id);
			if (error) {
				Logger.error(
					error,
					{},
					"Error updating template item quantity"
				);
				showNotification({
					message: "Fehler beim Aktualisieren des Vorlagenelements",
					type: "error",
				});
				useTemplatesStore.getState().fetchTemplates();
				return;
			}
			showNotification({
				message: "Position erfolgreich aktualisiert",
				type: "success",
			});
		} else {
			const { data: newItem, error } = await supabase
				.from(SupabaseTableEnum.TEMPLATE_ITEMS)
				.insert({
					template_id: templateId,
					code_e: item.code_e || "",
					quantity: item.quantity,
					tariffs_default_id: item.tariffs_default_id || null,
					tariffs_custom_id: item.tariffs_custom_id || null,
					article_id: item.article_id || null,
				})
				.select(COLUMNS_FOR_TEMPLATE_ITEM)
				.single();

			if (error) {
				Logger.error(error, {}, "Error adding template item");
				showNotification({
					message: "Fehler beim Hinzufügen des Vorlagenelements",
					type: "error",
				});
				return;
			}

			set(
				produce((state) => {
					state.templatesLookup[templateId].template_items.push(
						newItem
					);
				})
			);

			showNotification({
				message: "Vorlagenelement erfolgreich hinzugefügt",
				type: "success",
			});
		}
	},
	pasteTemplateItems: async (
		items: TemplateItemInput[],
		templateId: number,
		forceDuplicate: boolean = false
	) => {
		const existingItems =
			useTemplatesStore.getState().templatesLookup[templateId]
				.template_items;
		const newItems: TemplateItemInput[] = [];
		const itemsToUpdate: { id: number; quantityToAdd: number }[] = [];
		const matchedIds = new Set<number>();

		items.forEach((item) => {
			if (forceDuplicate) {
				newItems.push(item);
			} else {
				const existing = findExistingItem(
					existingItems,
					item,
					matchedIds
				);

				if (existing) {
					matchedIds.add(existing.id);
					itemsToUpdate.push({
						id: existing.id,
						quantityToAdd: item.quantity,
					});
				} else {
					newItems.push(item);
				}
			}
		});

		if (itemsToUpdate.length > 0) {
			set(
				produce((state) => {
					itemsToUpdate.forEach(({ id, quantityToAdd }) => {
						const item = state.templatesLookup[
							templateId
						].template_items.find(
							(item: TemplateItem) => item.id === id
						);
						if (item) {
							item.quantity += quantityToAdd;
						}
					});
				})
			);

			for (const { id, quantityToAdd } of itemsToUpdate) {
				const existingItem = existingItems.find(
					(item) => item.id === id
				);
				if (existingItem) {
					const { error } = await supabase
						.from(SupabaseTableEnum.TEMPLATE_ITEMS)
						.update({
							quantity: existingItem.quantity + quantityToAdd,
						})
						.eq("id", id);

					if (error) {
						Logger.error(
							error,
							{},
							"Error updating template item quantity"
						);
						showNotification({
							message:
								"Fehler beim Aktualisieren des Vorlagenelements",
							type: "error",
						});
						useTemplatesStore.getState().fetchTemplates();
						return;
					}
				}
			}
		}

		if (newItems.length > 0) {
			const { data: insertedItems, error } = await supabase
				.from(SupabaseTableEnum.TEMPLATE_ITEMS)
				.insert(
					newItems.map((item) => ({
						template_id: templateId,
						code_e: item.code_e || "",
						quantity: item.quantity,
						tariffs_default_id: item.tariffs_default_id || null,
						tariffs_custom_id: item.tariffs_custom_id || null,
						article_id: item.article_id || null,
					}))
				)
				.select(COLUMNS_FOR_TEMPLATE_ITEM);

			if (error || !insertedItems) {
				Logger.error(error, {}, "Error adding template items");
				showNotification({
					message: "Fehler beim Hinzufügen der Vorlagenelemente",
					type: "error",
				});
				return;
			}

			set(
				produce((state) => {
					state.templatesLookup[templateId].template_items.push(
						...insertedItems
					);
				})
			);
		}

		showNotification({
			message: "Positionen erfolgreich eingefügt",
			type: "success",
		});
	},

	checkTemplateUsage: async (filter: {
		tariffs_default_id?: string;
		tariffs_custom_id?: string;
		article_id?: number;
	}) => {
		const needsOrgFilter = !!filter.tariffs_default_id;
		const organizationId = needsOrgFilter
			? useCentralStore.getState().organization?.id
			: null;
		if (needsOrgFilter && !organizationId) {
			Logger.error("No organization id found", {});
			return [];
		}

		let query = supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.select(
				needsOrgFilter
					? "template_id, templates!inner(organization_id)"
					: "template_id"
			);
		if (filter.tariffs_default_id)
			query = query.eq("tariffs_default_id", filter.tariffs_default_id);
		if (filter.tariffs_custom_id)
			query = query.eq("tariffs_custom_id", filter.tariffs_custom_id);
		if (filter.article_id)
			query = query.eq("article_id", filter.article_id);
		if (needsOrgFilter)
			query = query.eq("templates.organization_id", organizationId!);

		const { data, error } = await query;
		if (error) {
			Logger.error(error, {}, "Error checking template usage");
			return [];
		}
		const uniqueIds = [
			...new Set((data || []).map((row: any) => row.template_id)),
		];
		return uniqueIds.filter((id): id is number => id !== null);
	},

	reassignTemplateItems: async (
		from: { tariffs_default_id?: string; tariffs_custom_id?: string },
		to: {
			tariffs_default_id?: string | null;
			tariffs_custom_id?: string | null;
		}
	) => {
		const needsOrgFilter = !!from.tariffs_default_id;
		const organizationId = needsOrgFilter
			? useCentralStore.getState().organization?.id
			: null;
		if (needsOrgFilter && !organizationId) {
			Logger.error("No organization id found", {});
			return 0;
		}

		let query = supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.select(
				needsOrgFilter ? "id, templates!inner(organization_id)" : "id"
			);
		if (from.tariffs_default_id)
			query = query.eq("tariffs_default_id", from.tariffs_default_id);
		if (from.tariffs_custom_id)
			query = query.eq("tariffs_custom_id", from.tariffs_custom_id);
		if (needsOrgFilter)
			query = query.eq("templates.organization_id", organizationId!);

		const { data: items, error: selectError } = await query;
		if (selectError || !items?.length) {
			if (selectError)
				Logger.error(
					selectError,
					{},
					"Error finding template items to reassign"
				);
			return 0;
		}

		const itemIds = items.map((item: any) => item.id);
		const { error: updateError } = await supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.update(to)
			.in("id", itemIds);

		if (updateError) {
			Logger.error(updateError, {}, "Error reassigning template items");
			showNotification({
				message: "Fehler beim Aktualisieren der Vorlagen",
				type: "error",
			});
			return 0;
		}

		await useTemplatesStore.getState().fetchTemplates();
		return itemIds.length;
	},

	removeItemFromTemplates: async (filter: {
		tariffs_custom_id?: string;
		article_id?: number;
	}) => {
		let query = supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.select("id");
		if (filter.tariffs_custom_id)
			query = query.eq("tariffs_custom_id", filter.tariffs_custom_id);
		if (filter.article_id)
			query = query.eq("article_id", filter.article_id);

		const { data: items, error: selectError } = await query;

		if (selectError || !items?.length) {
			if (selectError)
				Logger.error(
					selectError,
					{},
					"Error finding template items to remove"
				);
			return 0;
		}

		const itemIds = items.map((item) => item.id);
		const { error: deleteError } = await supabase
			.from(SupabaseTableEnum.TEMPLATE_ITEMS)
			.delete()
			.in("id", itemIds);

		if (deleteError) {
			Logger.error(deleteError, {}, "Error removing template items");
			showNotification({
				message: "Fehler beim Entfernen der Vorlagenelemente",
				type: "error",
			});
			return 0;
		}

		await useTemplatesStore.getState().fetchTemplates();
		return itemIds.length;
	},
}));
